import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import {
  Clock, Calendar, Briefcase, ChevronLeft, ChevronRight, X, Filter,
  CheckCircle, XCircle, AlertCircle, Edit2, Trash2, DollarSign, Globe,
  FileText, TrendingUp
} from 'lucide-react';
import { UserContext } from '../contexts/UserContext';

const MyTimesheets = () => {
  const { user } = useContext(UserContext);
  const useTaskBasedTimesheet = user?.useTaskBasedTimesheet || false;
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [error, setError] = useState('');
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [projects, setProjects] = useState([]);
  const [adminTypes, setAdminTypes] = useState([]);
  const [filterProject, setFilterProject] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [useCustomDateRange, setUseCustomDateRange] = useState(false);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [currency, setCurrency] = useState('EUR');
  const [currentRate, setCurrentRate] = useState(null);
  const [rateType, setRateType] = useState('hourly');
  const [loadingEarnings, setLoadingEarnings] = useState(false);
  const [countryDays, setCountryDays] = useState({});
  const [loadingCountryDays, setLoadingCountryDays] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractDetails, setContractDetails] = useState(null);
  const [amendments, setAmendments] = useState([]);
  const [loadingContract, setLoadingContract] = useState(false);

  useEffect(() => {
    fetchTimesheets();
    fetchProjects();
    fetchAdminTypes();
  }, [activeTab, selectedMonth, filterProject, filterDateFrom, filterDateTo, useCustomDateRange]);

  useEffect(() => { loadCountryDays(); }, []);
  useEffect(() => { if (timesheets.length > 0) calculateEarnings(); else setTotalEarnings(0); }, [timesheets]);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/user-active-projects/`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setProjects(response.data || []);
    } catch (error) { console.error('Error fetching projects:', error); }
  };

  const fetchAdminTypes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin-types/`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setAdminTypes((response.data || []).filter((at) => at.is_active));
    } catch (error) { console.error('Error fetching admin types:', error); }
  };

  const calculateEarnings = async () => {
    setLoadingEarnings(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const contractRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/contracts/`, { headers });
      const contracts = contractRes.data || [];
      const activeContract = contracts.find(c => c.status === 'active') || contracts[0];
      if (!activeContract) { setCurrentRate(null); setTotalEarnings(0); setLoadingEarnings(false); return; }
      setCurrency(activeContract.currency || 'EUR');
      setRateType(activeContract.rate_type || 'hourly');
      setCurrentRate(parseFloat(activeContract.current_rate) || null);
      const contractRateType = activeContract.rate_type || 'hourly';
      const initialRate = parseFloat(activeContract.initial_rate) || parseFloat(activeContract.current_rate) || 0;
      let amendments = [];
      try {
        const amendRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/contracts/${activeContract.id}/amendments/`, { headers });
        amendments = amendRes.data || [];
        amendments.sort((a, b) => new Date(b.effective_date) - new Date(a.effective_date));
      } catch (e) {}
      const ratePeriods = [];
      let nextPeriodStart = null;
      for (const amendment of amendments) {
        const effectiveDate = new Date(amendment.effective_date);
        const newRate = parseFloat(amendment.new_rate) || 0;
        ratePeriods.push({ start: effectiveDate, end: nextPeriodStart, rate: newRate });
        nextPeriodStart = new Date(effectiveDate.getTime() - 86400000);
      }
      const contractStart = new Date(activeContract.start_date);
      ratePeriods.push({ start: contractStart, end: nextPeriodStart, rate: initialRate });
      const getRateForDate = (date) => {
        for (const period of ratePeriods) {
          if (date >= period.start && (period.end === null || date <= period.end)) return period.rate;
        }
        return initialRate;
      };
      const parseWorkedHours = (workedHours) => {
        if (!workedHours) return 0;
        const hoursMatch = workedHours.match(/(\d+)h/);
        const minsMatch = workedHours.match(/(\d+)min/);
        return (hoursMatch ? parseInt(hoursMatch[1]) : 0) + (minsMatch ? parseInt(minsMatch[1]) : 0) / 60;
      };
      let total = 0;
      const uniqueDaysPerRate = {};
      for (const ts of timesheets) {
        const date = new Date(ts.date);
        const workedHours = parseWorkedHours(ts.worked_hours);
        const rate = getRateForDate(date);
        if (contractRateType === 'hourly') total += workedHours * rate;
        else if (contractRateType === 'daily') {
          if (!uniqueDaysPerRate[rate]) uniqueDaysPerRate[rate] = new Set();
          uniqueDaysPerRate[rate].add(ts.date);
        } else if (contractRateType === 'monthly') total += (workedHours / 160) * rate;
      }
      if (contractRateType === 'daily') {
        for (const [rate, days] of Object.entries(uniqueDaysPerRate)) total += days.size * parseFloat(rate);
      }
      setTotalEarnings(total);
    } catch (error) { console.error('Error calculating earnings:', error); }
    finally { setLoadingEarnings(false); }
  };

  const loadCountryDays = async () => {
    setLoadingCountryDays(true);
    try {
      const token = localStorage.getItem('access_token');
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const userId = userData.id || userData.user_id;
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const [planRes, projectsRes, travelsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/resource-plan-publish-history/`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/projects/`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/travel/`, { headers }).catch(() => ({ data: [] }))
      ]);
      const planData = planRes.data || [];
      if (planData.length === 0) { setCountryDays({}); setLoadingCountryDays(false); return; }
      const latestPlan = planData[0];
      if (!latestPlan?.published_data?.assignments) { setCountryDays({}); setLoadingCountryDays(false); return; }
      const projectCountries = {};
      (projectsRes.data || []).forEach(p => { if (p.id && p.country) projectCountries[p.id] = p.country; });
      const allAssignments = latestPlan.published_data.assignments || [];
      const userIdInt = parseInt(userId) || 0;
      const days = {};
      const userAssignments = [];
      for (const assignment of allAssignments) {
        const assignmentUserId = assignment.user_id;
        if (assignmentUserId !== userIdInt && String(assignmentUserId) !== String(userId)) continue;
        userAssignments.push(assignment);
        let country = assignment.project_country || assignment.country;
        if (!country && assignment.project_id) country = projectCountries[assignment.project_id];
        if (!country) continue;
        const startDate = new Date(assignment.start_date);
        const endDate = new Date(assignment.end_date);
        const dayCount = Math.ceil((endDate - startDate) / 86400000) + 1;
        days[country] = (days[country] || 0) + dayCount;
      }
      const travels = travelsRes.data || [];
      for (const travel of travels) {
        const travelUserId = travel.user || travel.user_id;
        if (travelUserId !== userIdInt && String(travelUserId) !== String(userId)) continue;
        const travelType = travel.travel_type;
        if (travelType !== 'check_in' && travelType !== 'check_out') continue;
        const travelDate = new Date(travel.start_date || travel.departure_date);
        for (const assignment of userAssignments) {
          const projectId = assignment.project_id;
          const country = projectCountries[projectId];
          if (!country) continue;
          const projectStart = new Date(assignment.start_date);
          const projectEnd = new Date(assignment.end_date);
          if (travelType === 'check_in') {
            const daysBefore = (projectStart - travelDate) / 86400000;
            if (daysBefore >= 0 && daysBefore <= 2) { days[country] = (days[country] || 0) + 1; break; }
          } else if (travelType === 'check_out') {
            const daysAfter = (travelDate - projectEnd) / 86400000;
            if (daysAfter >= 0 && daysAfter <= 2) { days[country] = (days[country] || 0) + 1; break; }
          }
        }
      }
      setCountryDays(days);
    } catch (error) { console.error('Error loading country days:', error); }
    finally { setLoadingCountryDays(false); }
  };

  const getCountryCode = (country) => {
    if (!country) return '--';
    const map = { 'poland': 'PL', 'polska': 'PL', 'germany': 'DE', 'niemcy': 'DE', 'deutschland': 'DE', 'france': 'FR', 'francja': 'FR', 'united kingdom': 'UK', 'uk': 'UK', 'netherlands': 'NL', 'holandia': 'NL', 'belgium': 'BE', 'belgia': 'BE', 'denmark': 'DK', 'dania': 'DK', 'sweden': 'SE', 'szwecja': 'SE', 'norway': 'NO', 'norwegia': 'NO', 'finland': 'FI', 'finlandia': 'FI', 'spain': 'ES', 'hiszpania': 'ES', 'italy': 'IT', 'włochy': 'IT', 'austria': 'AT', 'czech republic': 'CZ', 'czechy': 'CZ', 'switzerland': 'CH', 'szwajcaria': 'CH', 'ireland': 'IE', 'irlandia': 'IE', 'portugal': 'PT', 'portugalia': 'PT', 'lithuania': 'LT', 'litwa': 'LT', 'latvia': 'LV', 'łotwa': 'LV', 'estonia': 'EE', 'romania': 'RO', 'rumunia': 'RO', 'bulgaria': 'BG', 'bułgaria': 'BG', 'hungary': 'HU', 'węgry': 'HU', 'croatia': 'HR', 'chorwacja': 'HR', 'slovakia': 'SK', 'słowacja': 'SK', 'slovenia': 'SI', 'słowenia': 'SI' };
    return map[country.toLowerCase().trim()] || country.substring(0, 2).toUpperCase();
  };

  const getCountryFlag = (code) => {
    if (!code || code.length !== 2 || code === '--') return '🏳️';
    return String.fromCodePoint(code.charCodeAt(0) - 65 + 0x1F1E6, code.charCodeAt(1) - 65 + 0x1F1E6);
  };

  const getCurrencySymbol = (curr) => ({ 'EUR': '€', 'PLN': 'zł', 'USD': '$', 'GBP': '£' }[curr] || curr);

  const showContractDetails = async () => {
    setShowContractModal(true);
    setLoadingContract(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const contractRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/contracts/`, { headers });
      const contracts = contractRes.data || [];
      const activeContract = contracts.find(c => c.status === 'active') || contracts[0];
      if (!activeContract) { setContractDetails(null); setAmendments([]); setLoadingContract(false); return; }
      setContractDetails(activeContract);
      try {
        const amendRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/contracts/${activeContract.id}/amendments/`, { headers });
        const amendmentsList = amendRes.data || [];
        amendmentsList.sort((a, b) => new Date(b.effective_date) - new Date(a.effective_date));
        setAmendments(amendmentsList);
      } catch (e) { setAmendments([]); }
    } catch (error) { setContractDetails(null); setAmendments([]); }
    finally { setLoadingContract(false); }
  };

  const formatContractDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return dateStr; }
  };

  const getRateTypeLabel = (type) => ({ 'hourly': 'per hour', 'daily': 'per day', 'monthly': 'per month' }[type] || type);

  const fetchTimesheets = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      const params = { status: activeTab };
      if (useCustomDateRange) {
        if (filterDateFrom) params.date_from = filterDateFrom;
        if (filterDateTo) params.date_to = filterDateTo;
      } else { params.month = selectedMonth; }
      if (filterProject) params.projekt = filterProject;
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/my-timesheets/`, {
        params, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setTimesheets(response.data || []);
    } catch (error) { setError('Failed to load timesheets. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (timesheetId) => {
    if (!window.confirm('Are you sure you want to delete this timesheet?')) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/my-timesheets/${timesheetId}/`, { headers: { Authorization: `Bearer ${token}` } });
      fetchTimesheets();
    } catch (error) { alert('Failed to delete timesheet. ' + (error.response?.data?.error || 'Please try again.')); }
  };

  const handleEdit = (timesheet) => {
    setEditingTimesheet(timesheet);
    setEditForm({
      projekt: timesheet.projekt?.id || '', date: timesheet.date, start_time: timesheet.start_time,
      end_time: timesheet.end_time, break_duration: timesheet.break_duration, type: timesheet.type,
      admin_type: timesheet.admin_type?.id || '', note: timesheet.note || ''
    });
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      Object.entries(editForm).forEach(([key, value]) => formData.append(key, value.toString()));
      if (editingTimesheet.status === 'rejected') {
        formData.append('status', 'pending');
        formData.append('rejection_reason', '');
        formData.append('is_rejected', 'false');
      }
      await axios.put(`${process.env.REACT_APP_API_URL}/api/my-timesheets/${editingTimesheet.id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setEditingTimesheet(null);
      setEditForm(null);
      fetchTimesheets();
    } catch (error) { alert('Failed to update timesheet. ' + (error.response?.data?.error || 'Please try again.')); }
  };

  const calculateWorkedHours = (startTime, endTime, breakDuration) => {
    if (!startTime || !endTime) return '0h 0min';
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    let totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    if (totalMinutes < 0) totalMinutes += 24 * 60;
    totalMinutes -= breakDuration;
    return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}min`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-50 text-amber-600',
      approved: 'bg-emerald-50 text-emerald-600',
      rejected: 'bg-red-50 text-red-600'
    };
    const dots = { pending: 'bg-amber-500', approved: 'bg-emerald-500', rejected: 'bg-red-500' };
    const labels = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded-md ${styles[status] || styles.pending}`}>
        <span className={`w-1 h-1 rounded-full ${dots[status] || dots.pending}`} />
        {labels[status] || 'Pending'}
      </span>
    );
  };

  const changeMonth = (direction) => {
    const date = new Date(selectedMonth + '-01');
    date.setMonth(date.getMonth() + (direction === 'prev' ? -1 : 1));
    setSelectedMonth(date.toISOString().slice(0, 7));
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const getTypeDisplay = (ts) => {
    // Task-based timesheet - use task_category_name and task_subcategory_name if available
    if (useTaskBasedTimesheet) {
      // First try the dedicated task fields from backend
      if (ts.task_category_name || ts.task_subcategory_name) {
        if (ts.task_subcategory_name && ts.task_category_name) {
          return `${ts.task_category_name} / ${ts.task_subcategory_name}`;
        }
        return ts.task_subcategory_name || ts.task_category_name;
      }
      // Fallback to category/type fields (legacy data)
      if (ts.type && ts.category && ts.type !== ts.category) {
        return `${ts.category} / ${ts.type}`;
      }
      return ts.type || ts.category || '-';
    }

    // Standard timesheet - show work type with friendly names
    const typeNames = { 'Work(Day)': 'Project Work (Day)', 'Rework 50': 'Rework 50% (Day)', 'HSE': 'HSE (Day)', 'Work(Night)': 'Project Work (Night)', 'HSE NIGHT': 'HSE (Night)', 'Rework(Night)': 'Rework 50% (Night)', 'Travel 50': 'Traveling 50%', 'Travel 100': 'Traveling 100%', 'Company Phone': 'Company Phone', 'Administr': 'Administration (Day)', 'AdministrationLongRotation': 'Administration Long Rotation', 'Administration(Night)': 'Administration (Night)', 'FillupHou': 'Fillup Hours', 'LongRotation': 'Long Rotation(Day)', 'LongRotat': 'Long Rotation (Night)', 'Training Online': 'Training Online', 'Training Level Vestas': 'Training Level Vestas', 'Training GWO': 'Training GWO', 'Training International': 'Training International', 'Training Poland': 'Training Poland' };
    const mainType = typeNames[ts.type] || ts.type;
    return ts.admin_type?.name ? `${mainType} - ${ts.admin_type.name}` : mainType;
  };

  const getMonthName = (monthStr) => new Date(monthStr + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

  const calculateTotalHours = () => {
    let totalMinutes = 0;
    timesheets.forEach(ts => {
      const [hours, mins] = ts.worked_hours.replace('h', '').replace('min', '').trim().split(' ');
      totalMinutes += parseInt(hours || 0) * 60 + parseInt(mins || 0);
    });
    return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}min`;
  };

  const hasFilters = filterProject || filterDateFrom || filterDateTo;

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm border border-steel-100">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-slate-600 text-sm font-medium">Loading timesheets...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black text-slate-900">My Timesheets</h1>
          <p className="text-xs text-slate-500">View and manage your submitted timesheets</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2">
        {['all', 'pending', 'approved', 'rejected'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`h-9 px-4 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${
              activeTab === tab
                ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20'
                : 'bg-white border border-steel-200 text-slate-600 hover:bg-steel-50'
            }`}
          >
            {tab === 'all' ? 'All' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-brand-200 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-steel-100 flex items-center justify-center">
            <Filter className="h-4 w-4 text-slate-600" />
          </div>
          <h3 className="text-sm font-black text-slate-900 uppercase">Filters</h3>
          <span className="ml-auto px-2 py-0.5 text-[9px] font-bold bg-steel-100 text-slate-600 rounded-md">
            {timesheets.length} results
          </span>
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          {/* Date Mode Toggle */}
          <div>
            <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Date Filter</label>
            <div className="flex">
              <button
                onClick={() => setUseCustomDateRange(false)}
                className={`h-9 px-3 text-xs font-bold rounded-l-xl border transition-colors ${!useCustomDateRange ? 'bg-brand-500 text-white border-brand-500' : 'bg-white border-steel-200 text-slate-600'}`}
              >
                Month
              </button>
              <button
                onClick={() => setUseCustomDateRange(true)}
                className={`h-9 px-3 text-xs font-bold rounded-r-xl border-t border-r border-b transition-colors ${useCustomDateRange ? 'bg-brand-500 text-white border-brand-500' : 'bg-white border-steel-200 text-slate-600'}`}
              >
                Range
              </button>
            </div>
          </div>

          {/* Month Navigation or Date Range */}
          {!useCustomDateRange ? (
            <div>
              <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Month</label>
              <div className="flex items-center gap-1">
                <button onClick={() => changeMonth('prev')} className="h-9 w-9 flex items-center justify-center rounded-lg bg-steel-100 hover:bg-steel-200 transition-colors">
                  <ChevronLeft size={16} className="text-slate-600" />
                </button>
                <span className="h-9 px-3 flex items-center text-sm font-bold text-slate-900 bg-steel-50 rounded-lg min-w-[140px] justify-center">
                  {getMonthName(selectedMonth)}
                </span>
                <button onClick={() => changeMonth('next')} className="h-9 w-9 flex items-center justify-center rounded-lg bg-steel-100 hover:bg-steel-200 transition-colors">
                  <ChevronRight size={16} className="text-slate-600" />
                </button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">From</label>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className={`h-9 px-3 border rounded-xl text-sm font-medium focus:ring-1 focus:ring-brand-500 ${filterDateFrom ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-slate-700'}`}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">To</label>
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className={`h-9 px-3 border rounded-xl text-sm font-medium focus:ring-1 focus:ring-brand-500 ${filterDateTo ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-slate-700'}`}
                />
              </div>
            </>
          )}

          {/* Project Filter */}
          <div>
            <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Project</label>
            <select
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              className={`h-9 px-3 min-w-[140px] border rounded-xl text-sm font-medium focus:ring-1 focus:ring-brand-500 ${filterProject ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-slate-700'}`}
            >
              <option value="">All Projects</option>
              {projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
            </select>
          </div>

          {/* Clear Filters */}
          {hasFilters && (
            <button
              onClick={() => { setFilterProject(''); setFilterDateFrom(''); setFilterDateTo(''); setUseCustomDateRange(false); }}
              className="h-9 px-3 bg-red-50 text-red-600 border border-red-200 rounded-xl text-xs font-bold hover:bg-red-100 transition-colors flex items-center gap-1"
            >
              <X size={14} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Content */}
      {timesheets.length === 0 ? (
        <div className="bg-white rounded-2xl border border-steel-100 shadow-sm p-10 text-center">
          <div className="w-12 h-12 bg-steel-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Clock className="h-6 w-6 text-slate-400" />
          </div>
          <p className="text-sm font-bold text-slate-900 mb-1">No timesheets found</p>
          <p className="text-xs text-slate-400">Try adjusting your filters or submit your first timesheet!</p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4">
            {/* Total Hours */}
            <div className="bg-white rounded-2xl p-4 border border-steel-100 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-brand-100 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-brand-500" />
                </div>
                <div>
                  <p className="text-2xl font-black text-slate-900">{calculateTotalHours()}</p>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Total Hours</p>
                  <p className="text-[10px] text-slate-400">{timesheets.length} timesheet{timesheets.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>

            {/* Earnings */}
            <div
              className="bg-white rounded-2xl p-4 border border-steel-100 shadow-sm cursor-pointer hover:border-brand-200 transition-colors"
              onClick={showContractDetails}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-emerald-500" />
                </div>
                <div className="flex-1">
                  {loadingEarnings ? (
                    <p className="text-2xl font-black text-slate-400">...</p>
                  ) : currentRate ? (
                    <p className="text-2xl font-black text-emerald-600">{totalEarnings.toFixed(2)} {getCurrencySymbol(currency)}</p>
                  ) : (
                    <p className="text-lg font-bold text-slate-400">No Contract</p>
                  )}
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Earnings</p>
                </div>
                <div
                  className="flex items-center gap-1 px-2 py-1 bg-violet-100 rounded-lg cursor-pointer hover:bg-violet-200 transition-colors"
                  onClick={(e) => { e.stopPropagation(); if (Object.keys(countryDays).length > 0) setShowCountryModal(true); }}
                >
                  <Globe size={14} className="text-violet-500" />
                  <span className="text-xs font-bold text-violet-600">
                    {loadingCountryDays ? '...' : `${Object.values(countryDays).reduce((a, b) => a + b, 0)}d`}
                  </span>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="bg-white rounded-2xl p-4 border border-steel-100 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-amber-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-lg font-black text-emerald-600">{timesheets.filter(t => t.status === 'approved').length}</p>
                      <p className="text-[9px] font-bold text-slate-400 uppercase">Approved</p>
                    </div>
                    <div>
                      <p className="text-lg font-black text-amber-600">{timesheets.filter(t => t.status === 'pending').length}</p>
                      <p className="text-[9px] font-bold text-slate-400 uppercase">Pending</p>
                    </div>
                    <div>
                      <p className="text-lg font-black text-red-600">{timesheets.filter(t => t.status === 'rejected').length}</p>
                      <p className="text-[9px] font-bold text-slate-400 uppercase">Rejected</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl border border-brand-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-steel-50 border-b border-steel-100">
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-700 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-700 uppercase tracking-wider">Project</th>
                    {useTaskBasedTimesheet && (
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-700 uppercase tracking-wider">Turbine</th>
                    )}
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-700 uppercase tracking-wider">Time</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-700 uppercase tracking-wider">Break</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-700 uppercase tracking-wider">Hours</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-700 uppercase tracking-wider">{useTaskBasedTimesheet ? 'Category/Task' : 'Type'}</th>
                    <th className="px-4 py-3 text-center text-[10px] font-bold text-slate-700 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-center text-[10px] font-bold text-slate-700 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {timesheets.map((ts) => (
                    <React.Fragment key={ts.id}>
                      <tr className="hover:bg-steel-50 transition-colors border-t border-steel-100">
                        <td className="px-4 py-3 text-sm text-slate-700">{formatDate(ts.date)}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{ts.projekt?.name || 'N/A'}</td>
                        {useTaskBasedTimesheet && (
                          <td className="px-4 py-3 text-sm text-slate-700">{ts.project_turbine_name || '-'}</td>
                        )}
                        <td className="px-4 py-3 text-sm text-slate-700">{ts.start_time} - {ts.end_time}</td>
                        <td className="px-4 py-3 text-sm text-slate-500">{ts.break_duration} min</td>
                        <td className="px-4 py-3 text-sm font-bold text-slate-900">{ts.worked_hours}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 text-[9px] font-bold bg-violet-100 text-violet-600 rounded-md">{getTypeDisplay(ts)}</span>
                        </td>
                        <td className="px-4 py-3 text-center">{getStatusBadge(ts.status)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            {(ts.status === 'pending' || ts.status === 'rejected') && (
                              <>
                                <button
                                  onClick={() => handleEdit(ts)}
                                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-brand-50 text-brand-500 hover:bg-brand-100 transition-colors"
                                  title={ts.status === 'rejected' ? 'Edit and resubmit' : 'Edit'}
                                >
                                  <Edit2 size={14} />
                                </button>
                                <button
                                  onClick={() => handleDelete(ts.id)}
                                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {ts.note && (
                        <tr className="bg-white">
                          <td colSpan={useTaskBasedTimesheet ? 9 : 8} className="px-4 pt-0 pb-3">
                            <p className="text-xs text-slate-500"><strong className="text-slate-700">Note:</strong> {ts.note}</p>
                          </td>
                        </tr>
                      )}
                      {ts.rejection_reason && (
                        <tr className="bg-red-50">
                          <td colSpan={useTaskBasedTimesheet ? 9 : 8} className="px-4 pt-0 pb-3">
                            <p className="text-xs text-red-600"><strong>Rejection Reason:</strong> {ts.rejection_reason}</p>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Edit Modal */}
      {editingTimesheet && editForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => { setEditingTimesheet(null); setEditForm(null); }} />
          <div className="relative bg-white rounded-2xl w-full max-w-2xl shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
                  <Edit2 className="h-4 w-4 text-brand-500" />
                </div>
                <h2 className="text-sm font-black text-slate-900 uppercase">
                  {editingTimesheet.status === 'rejected' ? 'Edit & Resubmit' : 'Edit Timesheet'}
                </h2>
              </div>
              <button onClick={() => { setEditingTimesheet(null); setEditForm(null); }} className="p-1.5 rounded-lg hover:bg-steel-100 text-slate-400">
                <X size={18} />
              </button>
            </div>

            {editingTimesheet.status === 'rejected' && editingTimesheet.rejection_reason && (
              <div className="px-4 py-2 bg-red-50 border-b border-red-100">
                <p className="text-xs text-red-600"><strong>Previous rejection:</strong> {editingTimesheet.rejection_reason}</p>
              </div>
            )}

            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">Project *</label>
                  <select
                    value={editForm.projekt}
                    onChange={(e) => setEditForm({ ...editForm, projekt: e.target.value })}
                    className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="">-- Select project --</option>
                    {projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">Date *</label>
                  <input
                    type="date"
                    value={editForm.date}
                    onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                    className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">Start Time *</label>
                  <input
                    type="time"
                    value={editForm.start_time}
                    onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                    className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">End Time *</label>
                  <input
                    type="time"
                    value={editForm.end_time}
                    onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })}
                    className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">Break (min) *</label>
                  <input
                    type="number"
                    value={editForm.break_duration}
                    onChange={(e) => setEditForm({ ...editForm, break_duration: parseInt(e.target.value) || 0 })}
                    min="0"
                    max="480"
                    className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">Worked Hours</label>
                  <div className="px-3 py-2.5 bg-steel-50 border border-steel-200 rounded-xl text-sm font-bold text-brand-600">
                    {calculateWorkedHours(editForm.start_time, editForm.end_time, editForm.break_duration)}
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">Type *</label>
                  <select
                    value={editForm.type}
                    onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                    className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="Work(Day)">Project Work (Day)</option>
                    <option value="Work(Night)">Project Work (Night)</option>
                    <option value="Rework 50">Rework 50% (Day)</option>
                    <option value="Rework(Night)">Rework 50% (Night)</option>
                    <option value="HSE">HSE (Day)</option>
                    <option value="HSE NIGHT">HSE (Night)</option>
                    <option value="LongRotation">Long Rotation (Day)</option>
                    <option value="LongRotat">Long Rotation (Night)</option>
                    <option value="Travel 50">Traveling 50%</option>
                    <option value="Travel 100">Traveling 100%</option>
                    <option value="Company Phone">Company Phone</option>
                    <option value="FillupHou">Fillup Hours</option>
                    <option value="Administr">Administration (Day)</option>
                    <option value="Administration(Night)">Administration (Night)</option>
                    <option value="AdministrationLongRotation">Administration Long Rotation</option>
                    <option value="Training Online">Training Online</option>
                    <option value="Training International">Training International</option>
                    <option value="Training Poland">Training Poland</option>
                    <option value="Training GWO">Training GWO</option>
                    <option value="Training Level Vestas">Training Level Vestas</option>
                  </select>
                </div>
                {(editForm.type === 'Administr' || editForm.type === 'Administration(Night)' || editForm.type === 'AdministrationLongRotation') && (
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">Admin Type *</label>
                    <select
                      value={editForm.admin_type}
                      onChange={(e) => setEditForm({ ...editForm, admin_type: e.target.value })}
                      className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    >
                      <option value="">-- Select --</option>
                      {adminTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">Note</label>
                <textarea
                  value={editForm.note}
                  onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                  rows={3}
                  maxLength={255}
                  placeholder="Optional note..."
                  className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
                />
              </div>
            </div>

            <div className="px-4 py-3 border-t border-steel-100 bg-steel-50 flex items-center justify-end gap-2">
              <button onClick={() => { setEditingTimesheet(null); setEditForm(null); }} className="h-9 px-4 text-slate-600 rounded-xl hover:bg-steel-200 font-bold text-xs transition-colors">
                Cancel
              </button>
              <button onClick={handleSaveEdit} className="h-9 px-4 bg-brand-500 text-white rounded-xl hover:bg-brand-600 font-bold text-xs uppercase tracking-wide transition-all shadow-lg shadow-brand-500/20">
                {editingTimesheet.status === 'rejected' ? 'Resubmit' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Country Days Modal */}
      {showCountryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowCountryModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <Globe className="h-4 w-4 text-violet-600" />
                </div>
                <h2 className="text-sm font-black text-slate-900 uppercase">Scheduled Days by Country</h2>
              </div>
              <button onClick={() => setShowCountryModal(false)} className="p-1.5 rounded-lg hover:bg-steel-100 text-slate-400">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-brand-50 rounded-xl p-3 flex items-center justify-between">
                <span className="text-xs font-bold text-brand-700 uppercase">Total Scheduled Days</span>
                <span className="text-xl font-black text-brand-600">{Object.values(countryDays).reduce((a, b) => a + b, 0)} days</span>
              </div>
              <div className="space-y-2">
                {Object.entries(countryDays).sort((a, b) => b[1] - a[1]).map(([country, days]) => {
                  const code = getCountryCode(country);
                  const flag = getCountryFlag(code);
                  const total = Object.values(countryDays).reduce((a, b) => a + b, 0);
                  const percentage = Math.round((days / total) * 100);
                  return (
                    <div key={country} className="flex items-center gap-3 p-2 bg-steel-50 rounded-xl">
                      <span className="text-xl">{flag}</span>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-slate-900">{country}</p>
                        <div className="w-full h-1.5 bg-steel-200 rounded-full mt-1">
                          <div className="h-1.5 bg-brand-500 rounded-full" style={{ width: `${percentage}%` }} />
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-black text-slate-900">{days} days</p>
                        <p className="text-[10px] text-slate-400">{percentage}%</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-400 text-center">Based on your resource planning assignments and travel days</p>
            </div>
          </div>
        </div>
      )}

      {/* Contract Details Modal */}
      {showContractModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowContractModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-brand-500" />
                </div>
                <h2 className="text-sm font-black text-slate-900 uppercase">Contract Details</h2>
              </div>
              <button onClick={() => setShowContractModal(false)} className="p-1.5 rounded-lg hover:bg-steel-100 text-slate-400">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {loadingContract ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : !contractDetails ? (
                <div className="text-center py-6">
                  <div className="w-12 h-12 bg-steel-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <FileText className="h-6 w-6 text-slate-400" />
                  </div>
                  <p className="text-sm font-bold text-slate-900 mb-1">No Contract Found</p>
                  <p className="text-xs text-slate-400">Please contact your administrator.</p>
                </div>
              ) : (
                <>
                  <div className="bg-brand-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-black text-brand-600">{contractDetails.current_rate} {getCurrencySymbol(contractDetails.currency || 'EUR')}</p>
                    <p className="text-xs font-bold text-brand-700 uppercase">{getRateTypeLabel(contractDetails.rate_type || 'hourly')}</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-3 flex items-center justify-between">
                    <span className="text-xs font-bold text-emerald-700">Your Earnings ({getMonthName(selectedMonth)})</span>
                    <span className="text-lg font-black text-emerald-600">{totalEarnings.toFixed(2)} {getCurrencySymbol(contractDetails.currency || 'EUR')}</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-steel-50 rounded-lg">
                      <span className="text-xs text-slate-500">Status</span>
                      <span className={`px-2 py-0.5 text-[9px] font-bold uppercase rounded-md ${contractDetails.status === 'active' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                        {contractDetails.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-steel-50 rounded-lg">
                      <span className="text-xs text-slate-500">Start Date</span>
                      <span className="text-xs font-bold text-slate-900">{formatContractDate(contractDetails.start_date)}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-steel-50 rounded-lg">
                      <span className="text-xs text-slate-500">End Date</span>
                      <span className="text-xs font-bold text-slate-900">{contractDetails.is_indefinite ? 'Indefinite' : formatContractDate(contractDetails.end_date)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default MyTimesheets;
